import Button from "@mui/material/Button";
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import COLORS from "lib/colors";
import Typography from "@mui/material/Typography";
import { useFeatures } from "providers/FeaturesProvider";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/system/Box";
import LaunchAiDialog from "./LaunchAiDialog";
import { useCallback, useEffect, useState } from "react";
import AiChatDialog from "./AiChatDialog";
import Stack from "@mui/material/Stack";
import ChatPasteGoIcon from "components/UI/icons/ChatPasteGoIcon";
import { failOnHttpError } from "lib/utils";
import { Prompt } from "components/Formik/forms";

const A_REALISTIC_PAUSE = 1234

export interface ChatStatus {
  status: 'running' | 'queued' | 'fetching' | 'discarded' | 'succeeded'
  taskId?: string
  chatId?: string
  error?:  string
  url?: string
}

interface TimerStatus {
  timeRemaining: number
  isComplete: boolean
  timeout: ReturnType<typeof setTimeout> | null
}

type JSONValue =
  | string
  | number
  | boolean
  | null
  | JSONValue[]
  | { [key: string]: JSONValue }

type Message =
  | {
      role: 'assistant';
      content: string;
      images: string[];
      table: JSONValue | null
    }
  | {
      role: 'user' | 'error' | 'loading';
      content: string;
    };

function filterMessagesForSubmission(messages) {
  return messages.filter(message => (message.role === 'assistant') || (message.role === 'user'))
}

const isChatLoading = (chatStatus) => ['running', 'queued', 'fetching'].includes(chatStatus);

interface LaunchAiButtonProps {
  disabled?: boolean
  onClick?: () => void,
}

function LaunchAiButton({onClick=()=>{}, disabled=false}: LaunchAiButtonProps) {
  return(
    <Button onClick={onClick} disabled={disabled} variant='outlined' startIcon={<AnalyticsOutlinedIcon />} sx={{ color: COLORS.frenchBlue, borderColor: COLORS.beauBlue }}>
      <Typography variant='body1'>Launch AI Analysis</Typography>
    </Button>
  )
}

export default function AiChatContainer({ totalCount, loading, requestBodyJson }) {
  const { getFeatureConfig } = useFeatures()

  const [openLoadingDialog, setOpenLoadingDialog] = useState(false)
  const [openChatDialog, setOpenChatDialog] = useState(false)

  const [datasetId, setDatasetId] = useState()
  const [messages, setMessages] = useState<Message[]>([])

  const minimumLeadCount = getFeatureConfig('ai_analysis', 'minimum_lead_count', 1000)
  const hasTooFewLeads = totalCount < minimumLeadCount

  const initialTime = 60
  const [chatStatus, setChatStatus] = useState<ChatStatus|null>(null)
  const [timerStatus, setTimerStatus] = useState<TimerStatus>(() => ({ timeRemaining: initialTime, isComplete: false, timeout: null}))

  useEffect(() => {
    if(chatStatus?.status === 'running') {
      setTimeout(() => {
        setTimerStatus(oldValue => ({ ...oldValue, timeRemaining: oldValue.timeRemaining - 1 }))
      }, 1000)
    }
  }, [chatStatus?.status, timerStatus])

  const handleError = useCallback(() => {
    setTimeout(() => {
      setChatStatus({ status: 'discarded', error: 'Failed to schedule export job' })
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'error', content: 'Something went wrong. Please try again.' },
      ])
    }, A_REALISTIC_PAUSE * 2 )
  }, [])

  const handleChatSubmit = useCallback((inputValue) => {
    const createUserMessage = (content: string): Message => ({
      role: "user",
      content,
    })

    const withNewQuestion = [
      ...messages,
      createUserMessage(inputValue),
    ]
    setMessages(withNewQuestion)

    setTimeout(() => {
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'loading', content: '*VEGA is thinking...*' },
      ])
    }, A_REALISTIC_PAUSE)

    setChatStatus({ status: 'running' })
    const requestOptions = {
      method: "POST",
      body: JSON.stringify({
        dataset_id: datasetId,
        messages: filterMessagesForSubmission(withNewQuestion)
      }),
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    }

    fetch('/api/v1/leads-chats/new', requestOptions)
      .then(failOnHttpError)
      .then(response => response.json())
      .then(result => {
        setChatStatus({ status: 'queued', taskId: result.task_id })
      })
      .catch(_error => {
        handleError()
      })
  }, [datasetId, handleError, messages])

  const fetchChat = useCallback((chatId) => {
    if(!chatId) {
      return
    }

    fetch(`/api/v1/leads-chats/chat/${chatId}`)
      .then(failOnHttpError)
      .then(response => response.json())
      .then(result => {
        const withoutLoading = messages.filter(message => message.role !== 'loading')
        setMessages([...withoutLoading, ...result.messages])
        setChatStatus(chatStatus => ({ ...chatStatus, status: 'succeeded', chatId: undefined  }))
      })
      .catch(_error => {
        handleError()
      })
  }, [handleError, messages])

  const updateStatus = useCallback(() => {
    if(!chatStatus?.taskId) {
      return
    }

    fetch(`/api/v1/leads-chats/status/${chatStatus.taskId}`)
      .then(failOnHttpError)
      .then(response => response.json())
      .then(result => {
        if(result.status === 'SUCCEEDED' || result.status === 'FAILED') {
          setTimerStatus(oldValue => ({ ...oldValue, isComplete: true }))
          if(result.status === 'SUCCEEDED') {
            setChatStatus(chatStatus => ({ ...chatStatus, status: 'fetching', taskId: undefined }))
            fetchChat(result.chat_id)
          } else if (result.status === 'FAILED') {
            setChatStatus(chatStatus => ({ ...chatStatus, ...result, taskId: undefined  }))
          }
        } else {
          const timeout = setTimeout(() => {
            updateStatus()
          }, 500)
          setTimerStatus(oldValue => ({ ...oldValue, timeRemaining: oldValue.timeRemaining - 0.5, timeout: timeout }))
        }
      })
      .catch(_error => {
        handleError()
      })
  }, [chatStatus?.taskId, fetchChat, handleError])

  useEffect(() => {
    if (chatStatus?.taskId) {
      updateStatus();
    }
  }, [chatStatus?.taskId, updateStatus]);

  const handleLaunchComplete = useCallback((datasetId) => {
    setOpenLoadingDialog(false)
    setDatasetId(datasetId)
    setMessages([])
    setOpenChatDialog(true)
  }, [])

  return(
    <>
      <Prompt
        when={datasetId}
        message="You have an active AI Analysis launched. If you leave this page, you'll lose your chat history and will need to relaunch the AI to continue using it."
      />

      <Stack direction='row' gap={2}>
        {(loading || hasTooFewLeads) &&
            <Tooltip title={loading ? 'Loading...' : `Minimum ${minimumLeadCount} lead records required for AI Analysis`}>
              <Box>
                <LaunchAiButton disabled={true} />
              </Box>
            </Tooltip>
        ||
          <LaunchAiButton onClick={() => setOpenLoadingDialog(true)}/>
        }
        {datasetId &&
          <Button onClick={() => setOpenChatDialog(true)} disabled={openChatDialog} variant='contained' startIcon={<ChatPasteGoIcon />} sx={{  }}>
            <Typography variant='body1'>Open AI Chat</Typography>
          </Button>
        }
      </Stack>


      {openLoadingDialog &&
        <LaunchAiDialog onComplete={handleLaunchComplete} onClose={() => setOpenLoadingDialog(false)} datasetId={datasetId} setDatasetId={setDatasetId} requestBodyJson={requestBodyJson} />
      }

      {openChatDialog &&
        <AiChatDialog onClose={() => setOpenChatDialog(false)} messages={messages} onChatSubmit={(inputValue) => handleChatSubmit(inputValue)} loading={isChatLoading(chatStatus?.status)} />
      }
    </>
  )
}
